import React, { useState, useEffect, useCallback } from "react";
import useJWTInterceptors from "../authentication/useJWTInterceptors";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import PersianDateTimePicker from "../components/ComparingPersianDateTimePicker";
import { useSelector } from "react-redux";
import {
  formatDateAndTimeHourly,
  formatDateAndTimeHourlyAQMS, // hourly sound and hourly aqms are similar in format
} from "../components/DateAndTimeFunctions";
import NoAccessPage from "./NoAccessPage";

// Register necessary chart components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const convertToJalali = (x) => {
  if (!x) return "";
  const datePart = x.slice(0, 10);
  const timePart = x.slice(11, 19);
  const formattedDate = new Intl.DateTimeFormat("fa-IR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(datePart));
  return `${formattedDate} (${timePart})`;
};

const Comparison = () => {
  const isLoggedIn = useSelector((state) => state.login.accessToken);
  const [stationsLoading, setStationsLoading] = useState(true);
  const [stationsError, setStationsError] = useState(null);
  const [stations, setStations] = useState([]);
  const [paraInformation, setParaInformation] = useState([]);

  // Get current date and time in UTC
  const currentUtcDate = new Date();

  // Get the date for 3 days ago
  const threeDaysAgo = new Date(currentUtcDate);
  threeDaysAgo.setDate(currentUtcDate.getDate() - 3);

  const [selectedStartDateTime, setSelectedStartDateTime] =
    useState(threeDaysAgo);
  const [selectedEndDateTime, setSelectedEndDateTime] =
    useState(currentUtcDate);
  const [aqiType, setAqiType] = useState("AQI");
  const [aqmsStationInputs, setAqmsStationInputs] = useState([]);
  const [paraDeviceInputs, setParaDeviceInputs] = useState([
    { device: "", id: "" },
  ]);

  useEffect(() => {
    const fetchAqmsStations = async () => {
      try {
        const response = await useJWTInterceptors.get(
          "https://back.para-payesh.com/daq/air_aqms/?granularity=4"
        ); // Replace with your API endpoint
        setStations(response.data.data || []); // Ensure it’s always an array
        setStationsLoading(false);
      } catch (err) {
        setStationsError("Failed to fetch stations");
        setStationsLoading(false);
      }
    };

    const fetchParaInformation = async () => {
      try {
        // console.log("Fetching info...");
        const response = await useJWTInterceptors.get(
          "https://back.para-payesh.com/kwz/info"
        );
        setParaInformation(response.data.results); // Assuming the API returns a list of stations
        // console.log("info...", response.data.results);
      } catch (err) {}
    };

    fetchParaInformation();
    fetchAqmsStations();
  }, []);

  const handleAddStation = () => {
    setAqmsStationInputs([...aqmsStationInputs, { id: "", name: "" }]);
  };

  const handleAddDevice = () => {
    setParaDeviceInputs([...paraDeviceInputs, { device: "", id: "" }]);
  };

  const handleStationChange = (index, value) => {
    const updatedStations = [...aqmsStationInputs];
    updatedStations[index].id = value;
    setAqmsStationInputs(updatedStations);
  };

  const handleDeviceTypeChange = (index, value) => {
    const updatedDevices = [...paraDeviceInputs];
    updatedDevices[index].device = value;
    setParaDeviceInputs(updatedDevices);
  };

  const handleDeviceIdChange = (index, value) => {
    const updatedDevices = [...paraDeviceInputs];
    updatedDevices[index].id = value;
    setParaDeviceInputs(updatedDevices);
  };

  const handleAqiTypeChange = (e) => {
    // console.log("Selected Para ID:", e.target.value);
    setAqiType(e.target.value);
  };

  const [aqmsChartDATA, setAqmsChartData] = useState([]);
  const [paraChartDATA, setParaChartData] = useState([]);

  const [currentChartLabels, setCurrentChartLabels] = useState();
  const [currentChartData, setCurrentChartData] = useState();
  const [titleText, setTitleText] = useState("شاخص AQI");

  const [loadingAqms, setLoadingAqms] = useState(false); // Separate loading state for AQMS
  const [loadingPara, setLoadingPara] = useState(false); // Separate loading state for Para
  const [loading, setLoading] = useState(false); // Combined loading state
  const [errorAqms, setErrorAqms] = useState(null); // Separate error state for AQMS
  const [errorPara, setErrorPara] = useState(null); // Separate error state for Para
  const [error, setError] = useState(null); // Combined error state

  const fetchAqmsChartData = useCallback(
    async (selectedAqmsId) => {
      setErrorAqms(null); // Reset AQMS error
      setLoadingAqms(true); // Set AQMS loading to true

      const apiURL = `https://back.para-payesh.com/daq/history/${selectedAqmsId}/4?start_date=${formatDateAndTimeHourlyAQMS(
        selectedStartDateTime
      )}&end_date=${formatDateAndTimeHourlyAQMS(selectedEndDateTime)}`;

      try {
        const response = await useJWTInterceptors.get(apiURL);
        let allData = response.data.results;
        const totalCount = response.data.count;
        const totalPages = Math.ceil(totalCount / 50);

        // Fetch additional pages if needed
        for (let page = 2; page <= totalPages; page++) {
          const pageUrl = `${apiURL}&page=${page}`;
          const pageResponse = await useJWTInterceptors.get(pageUrl);
          allData = [...allData, ...pageResponse.data.results];
        }

        // Reverse the data array order
        allData.reverse();

        const selectedStation = stations.find(
          (station) => Number(station.device_id) === Number(selectedAqmsId)
        );

        // Add the data for this station to the state
        setAqmsChartData((prevData) => [
          ...prevData,
          {
            id: selectedAqmsId,
            name: selectedStation?.stationname || "", // Safely access stationname after validation
            data: allData,
          },
        ]);
      } catch (err) {
        setErrorAqms(err.message || "Error loading AQMS data");
      } finally {
        setLoadingAqms(false); // Set AQMS loading to false when done
      }
    },
    [stations, selectedStartDateTime, selectedEndDateTime]
  );

  const fetchParaChartData = useCallback(
    async (selectedParaDevice, selectedParaId) => {
      setErrorPara(null); // Reset Para error
      setLoadingPara(true); // Set Para loading to true

      const apiURL = `https://back.para-payesh.com/kwz/history/${selectedParaDevice}/${selectedParaId}/4?start_date=${formatDateAndTimeHourly(
        selectedStartDateTime
      )}&end_date=${formatDateAndTimeHourly(selectedEndDateTime)}`;
      console.log(apiURL);

      try {
        const response = await useJWTInterceptors.get(apiURL);
        let allData = response.data.results;

        const totalCount = response.data.count;
        const totalPages = Math.ceil(totalCount / 50);

        // Fetch additional pages if needed
        for (let page = 2; page <= totalPages; page++) {
          const pageUrl = `${apiURL}&page=${page}`;
          const pageResponse = await useJWTInterceptors.get(pageUrl);
          allData = [...allData, ...pageResponse.data.results];
        }

        // Reverse the data order
        allData.reverse();

        // Find the specific station
        let specificParaStation;
        if (selectedParaId && selectedParaDevice) {
          const ParaId = Number(selectedParaId); // Ensure selectedParaId is a number
          specificParaStation = paraInformation.find(
            (station) =>
              station.device_id === ParaId &&
              station.device === selectedParaDevice
          );
        }

        // Add the data for this para device and ID to the state
        setParaChartData((prevData) => [
          ...prevData,
          {
            name: specificParaStation.stationName || "",
            id: selectedParaId,
            data: allData,
          },
        ]);
      } catch (err) {
        setErrorPara("Error loading Para data");
      } finally {
        setLoadingPara(false); // Set Para loading to false when done
      }
    },
    [paraInformation, selectedStartDateTime, selectedEndDateTime]
  );

  // Combined error state to track both AQMS and Para errors
  useEffect(() => {
    if (errorAqms || errorPara) {
      // If any error occurs in AQMS or Para, update the combined error state
      setError(errorAqms || errorPara);
    } else {
      // Reset the combined error state if there is no error
      setError(null);
    }
  }, [errorAqms, errorPara]);

  // Combined loading state to track both AQMS and Para data loading
  useEffect(() => {
    if (!loadingAqms && !loadingPara) {
      setLoading(false); // Set combined loading to false only when both are done
    } else {
      setLoading(true); // Keep loading true if any data is still being fetched
    }
  }, [loadingAqms, loadingPara]);

  const onHandleSubmit = () => {
    // Check if the user has chosen at least one AQMS station or Para device
    const hasSelectedAqmsStation =
      Array.isArray(aqmsStationInputs) && aqmsStationInputs.length > 0;
    const hasSelectedParaDevice =
      Array.isArray(paraDeviceInputs) &&
      paraDeviceInputs.some((device) => device.device && device.id);

    if (!hasSelectedAqmsStation && !hasSelectedParaDevice) {
      alert("لطفاً حداقل یک ایستگاه را انتخاب کنید.");
      return; // Stop further execution
    }

    // Clear the previous chart data
    setAqmsChartData([]);
    setParaChartData([]);

    if (hasSelectedAqmsStation) {
      aqmsStationInputs.forEach((station) => fetchAqmsChartData(station.id));
    }

    if (hasSelectedParaDevice) {
      paraDeviceInputs.forEach((station) =>
        fetchParaChartData(station.device, station.id)
      );
    }
  };

  const predefinedColors = [
    "#3357FF",
    "#FF33A1",
    "#33FF57",
    "#A133FF",
    "#33FFF6",
    "#FFC733",
    "#FF3333",
    "#57FF33",
    "#3333FF",
    "#FF8333",
    "#8333FF",
    "#33FF83",
    "#3383FF",
    "#FF33F6",
    "#F6FF33",
    "#33F6FF",
    "#FF5733",
    "#A1FF33",
    "#FF33A1",
    "#5733FF",
    "#FF5733",
  ];

  const usedColors = new Set(); // Track used colors

  const getColor = (index) => {
    const color = predefinedColors[index % predefinedColors.length];
    if (usedColors.has(color)) return getColor(index + 1); // Avoid immediate repeats
    usedColors.add(color);
    return color;
  };

  useEffect(() => {
    // Helper function to find the dataset with the longest length
    const findLongestDataset = (chartData) => {
      if (!Array.isArray(chartData) || chartData.length === 0) return null;
      return chartData.reduce((longest, current) =>
        current.data.length > longest.data.length ? current : longest
      );
    };

    if (Array.isArray(paraChartDATA) && paraChartDATA.length > 0) {
      if (Array.isArray(aqmsChartDATA) && aqmsChartDATA.length > 0) {
        // Both paraChartDATA and aqmsChartDATA are available
        const longestParaData = findLongestDataset(paraChartDATA);
        const labels = longestParaData.data.map((item) =>
          convertToJalali(item.ccreated_datetime)
        );
        setCurrentChartLabels(labels);
      } else {
        // Only paraChartDATA is available
        const longestParaData = findLongestDataset(paraChartDATA);
        const labels = longestParaData.data.map((item) =>
          convertToJalali(item.ccreated_datetime)
        );
        setCurrentChartLabels(labels);
      }
    } else if (Array.isArray(aqmsChartDATA) && aqmsChartDATA.length > 0) {
      // Only aqmsChartDATA is available
      const longestAqmsData = findLongestDataset(aqmsChartDATA);
      const labels = longestAqmsData.data.map((item) =>
        convertToJalali(item.ccreated_datetime)
      );
      setCurrentChartLabels(labels);
    }
  }, [paraChartDATA, aqmsChartDATA]);

  useEffect(() => {
    // Combine datasets dynamically whenever dependencies change
    const aqmsChartDatasets = aqmsChartDATA.map((dataset, index) => ({
      data:
        aqiType === "PM25"
          ? dataset.data.map((data) => data.pm25)
          : aqiType === "PM10"
          ? dataset.data.map((data) => data.pm10)
          : dataset.data.map((data) => data.aqi),
      label: `ایستگاه ${dataset.name} (سامانه محیط زیست)`,
      fill: false,
      tension: 0.1,
      borderColor: getColor(index),
    }));

    const paraChartDatasets = paraChartDATA.map((dataset, index) => ({
      data:
        aqiType === "PM25"
          ? dataset.data.map((data) => data.PM25AQI)
          : aqiType === "PM10"
          ? dataset.data.map((data) => data.PM10AQI)
          : dataset.data.map((data) => data.AQI),
      label: `ایستگاه ${dataset.name} (شرکت فن پایا)`,
      fill: false,
      tension: 0.1,
      borderColor: getColor(aqmsChartDatasets.length + index),
    }));

    // Combine AQMS and Para datasets only if there is data available
    const combinedChartData = [...aqmsChartDatasets, ...paraChartDatasets];

    setCurrentChartData({
      labels: currentChartLabels || [], // Use currentChartLabels or fallback to empty array
      datasets: combinedChartData,
    });
  }, [aqiType, paraChartDATA, aqmsChartDATA, currentChartLabels]);

  useEffect(() => {
    if (aqiType === "PM25") {
      setTitleText("شاخص PM 2.5");
    } else if (aqiType === "PM10") {
      setTitleText("شاخص PM 10");
    } else {
      setTitleText("شاخص AQI");
    }
  }, [aqiType]);

  // Render conditionally based on whether the page is valid
  if (!isLoggedIn) {
    return <NoAccessPage />;
  }
  if (isLoggedIn) {
    return (
      <div className="container py-5 mx-auto " dir="rtl">
        <form
          onSubmit={(e) => {
            e.preventDefault(); // Prevents the default form submission
            onHandleSubmit(); // Calls your handle submit function
          }}
          className="container rounded shadow-lg p-5 bg-white"
          style={{ maxWidth: "800px", fontFamily: "Arial, sans-serif" }}
        >
          {/* Date Range Section */}
          <div className="mb-5">
            <h3 className=" mb-4">انتخاب بازه گزارش گیری</h3>

            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="start" className="form-label fw-semibold">
                  شروع بازه:
                </label>
                <PersianDateTimePicker
                  type="start"
                  defaultTime={selectedStartDateTime}
                  minTime={new Date("2024-01-01")}
                  maxTime={new Date(selectedEndDateTime)}
                  setStateFunction={setSelectedStartDateTime}
                  className="form-control shadow-sm"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="end" className="form-label fw-semibold">
                  پایان بازه:
                </label>
                <PersianDateTimePicker
                  type="end"
                  defaultTime={selectedEndDateTime}
                  minTime={new Date(selectedStartDateTime)}
                  maxTime={new Date(currentUtcDate)}
                  setStateFunction={setSelectedEndDateTime}
                  className="form-control shadow-sm"
                />
              </div>
            </div>
          </div>

          {/* Para-Payesh Device Section */}
          <div className="mb-5">
            <h3 className=" mb-4">انتخاب ایستگاه از سامانه پارا-پایش</h3>
            {paraDeviceInputs.map((deviceInput, index) => (
              <div className="row g-3 mb-3" key={`device-${index}`}>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">نوع دستگاه:</label>
                  <select
                    className="form-select shadow-sm"
                    value={deviceInput.device}
                    onChange={(e) =>
                      handleDeviceTypeChange(index, e.target.value)
                    }
                  >
                    <option value="" disabled>
                      انتخاب نوع دستگاه
                    </option>
                    <option value="SAPA">SAPA</option>
                    <option value="ESPS">ESPS</option>
                    <option value="OSPM">OSPM</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    سریال دستگاه:
                  </label>
                  <input
                    type="number"
                    className="form-control shadow-sm"
                    placeholder="سریال دستگاه"
                    min="100"
                    max="200"
                    value={deviceInput.id}
                    onChange={(e) =>
                      handleDeviceIdChange(index, e.target.value)
                    }
                  />
                </div>
              </div>
            ))}
            <button
              type="button"
              className="btn-primary shadow-sm rounded"
              onClick={handleAddDevice}
            >
              افزودن ایستگاه
            </button>
          </div>

          {/* AQMS Station Section */}
          <div className="mb-5">
            <h3 className=" mb-4">انتخاب ایستگاه از سامانه محیط زیست</h3>
            {stationsError && (
              <div className="alert alert-danger">
                در بارگذاری ایستگاه ها از سامانه محیط زیست خطایی رخ داده است،
                لطفا صفحه را به روز نمایی فرمایید.
              </div>
            )}
            {stationsLoading ? (
              <div className="alert alert-info">
                در حال بارگذاری ایستگاه ها...
              </div>
            ) : (
              aqmsStationInputs.map((stationInput, index) => (
                <div className="mb-4" key={`station-${index}`}>
                  <label className="form-label fw-semibold">
                    انتخاب ایستگاه AQMS:
                  </label>
                  <select
                    className="form-select shadow-sm"
                    value={stationInput.id}
                    onChange={(e) => handleStationChange(index, e.target.value)}
                  >
                    <option value="" disabled>
                      انتخاب ایستگاه
                    </option>
                    {stations.map((station) => (
                      <option key={station.device_id} value={station.device_id}>
                        {station.statename} - {station.stationname} -{" "}
                        {station.device_id}
                      </option>
                    ))}
                  </select>
                </div>
              ))
            )}
            <button
              type="button"
              className="btn-primary shadow-sm rounded"
              onClick={handleAddStation}
            >
              افزودن ایستگاه
            </button>
          </div>

          {/* AQI Parameter Section */}
          <div className="mb-5">
            <label className="form-label fw-semibold">انتخاب پارامتر:</label>
            <select
              className="form-select shadow-sm"
              value={aqiType}
              onChange={handleAqiTypeChange}
            >
              <option value="AQI">AQI</option>
              <option value="PM10">PM 10</option>
              <option value="PM25">PM 2.5</option>
            </select>
          </div>

          {/* Submit Button */}
          <div className="text-end">
            <button type="submit" className="btn btn-success shadow px-5 py-2">
              رسم نمودار
            </button>
          </div>
        </form>

        {loading ? (
          <div className="mx-auto d-flex align-items-center justify-content-center my-5">
            <span
              className="spinner spinner-border spinner-border-lg my-5"
              style={{ width: 70, height: 70 }}
            ></span>
          </div>
        ) : Array.isArray(currentChartLabels) &&
          currentChartLabels.length > 0 ? (
          <div className="chart-container my-5">
            <Line
              data={currentChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false, // Ensures it takes the full container height
                scales: {
                  x: { stacked: false },
                  y: { stacked: false },
                },
                plugins: {
                  title: {
                    display: true,
                    text: titleText,
                    font: {
                      size: 24, // Adjust this value to make the title bigger
                      weight: "bold", // Optional: Makes the title bold
                    },
                  },
                  legend: {
                    display: true,
                    labels: {
                      font: {
                        size: 18, // Adjust this value to make the legend text bigger
                        weight: "bold", // Optional: Makes the legend labels bold
                      },
                    },
                  },
                  datalabels: {
                    display: false, // Ensure data labels are hidden
                  },
                },
              }}
            />
          </div>
        ) : null}
      </div>
    );
  }
};

export default Comparison;
